import React, { useEffect, useState, useCallback } from 'react';
import { Search, Trash2, Building2, Mail, MapPin, User, Send, RefreshCw } from 'lucide-react';
import { sapi } from '../../lib/api';
import { ConfirmModal, Spinner, useToast, Toast } from '../../components/UI';
import { useNavigate } from 'react-router-dom';

interface Company {
  id: string; name: string; email: string;
  address: string; managerName: string;
  timesApplied: number; lastApplied: string;
}

export default function CompaniesPage(): React.ReactElement {
  const { toast, show } = useToast();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCompanies(await sapi.getCompanies()); }
    catch { setCompanies([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async () => {
    if (!deleteTarget) return;
    await sapi.deleteCompany(deleteTarget.id).catch(() => null);
    setCompanies(p => p.filter(c => c.id !== deleteTarget.id));
    show('Company removed', 'info'); setDeleteTarget(null);
  };

  // Navigate to Apply page with company details pre-filled via URL state
  const applyAgain = (c: Company) => {
    navigate('/student/apply', { state: { company: c } });
  };

  const filtered = companies.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || (c.managerName || '').toLowerCase().includes(q);
  });

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Companies</h1>
          <p className="page-sub">{companies.length} saved — auto-populated when you send an application</p>
        </div>
        <button className="btn btn-ghost" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Search */}
      <div className="search-wrap" style={{ maxWidth: 340, marginBottom: 20 }}>
        <Search size={14} />
        <input placeholder="Search by name, email or manager…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Building2 size={32} />
            <h3>{search ? 'No results found' : 'No companies yet'}</h3>
            <p>{search ? 'Try a different search.' : 'Companies are saved automatically every time you send an application.'}</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(c => (
            <div key={c.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Avatar */}
              <div style={{ width: 44, height: 44, borderRadius: 11, background: 'rgba(108,99,255,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                {(c.name || c.email).charAt(0).toUpperCase()}
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>{c.name || '(unnamed)'}</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} />{c.email}</span>
                  {c.managerName && <span style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}><User size={11} />{c.managerName}</span>}
                  {c.address && <span style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={11} />{c.address}</span>}
                </div>
              </div>

              {/* Stats */}
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 20, color: 'var(--accent)' }}>{c.timesApplied}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Applied</div>
              </div>

              <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--text3)', textAlign: 'right', minWidth: 70 }}>
                {new Date(c.lastApplied).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-primary btn-sm" onClick={() => applyAgain(c)} title="Apply again with this company">
                  <Send size={12} /> Apply Again
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(c)}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Remove Company"
          message={<>Remove <strong>{deleteTarget.name || deleteTarget.email}</strong> from your saved companies?</>}
          onConfirm={del} onCancel={() => setDeleteTarget(null)} danger
        />
      )}
    </div>
  );
}
